/**
 * Execution Slice
 *
 * Fetches single execution detail via POST /executions/fetch.
 * Used by TransmissionDetailPanel when viewing an execution's lifecycle.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { executionInitialState, ExecutionResponse } from './executionState';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';

/** Fetches a single execution by ID. */
export const fetchExecution = createAsyncThunk<
  ExecutionResponse, string, { state: RootState; rejectValue: string }
>('execution/fetch', async (executionId, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    const result = await beaconApi.fetchExecutions([executionId], apiBase);
    if (result.executions.length === 0) {
      return rejectWithValue(`Execution not found: ${executionId}`);
    }
    return result.executions[0];
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch execution');
  }
});

const executionSlice = createSlice({
  name: 'execution',
  initialState: executionInitialState,
  reducers: {
    clearExecution: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.currentRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExecution.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchExecution.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(fetchExecution.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to fetch execution';
      });
  },
});

export const { clearExecution } = executionSlice.actions;
export default executionSlice.reducer;
